// ReassignPanel — Phase R7 (2026-05-13) rewrite.
//
// Replaces the old comma-separated-IDs textbox with a filter-and-
// select flow:
//
//   1. Pick mode: Players or Sub-agents
//   2. Filter rows (free-text, current-parent typeahead, pincode)
//   3. Multi-select via checkboxes (each row shows eligibility chip:
//        ✓ eligible · ⚠ pending recharge · ⚠ pending withdrawal)
//   4. Pick a destination agent (typeahead, filtered to only agents
//      whose "Accepting players" / "Accepting sub-agents" toggle is ON)
//   5. Reason → password confirm → apply
//
// Backend safety nets (added in same phase):
//   * /admin/bulk-reassign apply re-filters ineligible IDs before
//     UPDATE and re-checks the destination toggle.
//   * Response carries `affected`, `requested`, and the `skipped`
//     breakdown so the UI can show "moved 12 of 15, skipped 3".
//
// The GeoAutoReassignCard (Phase R7 weekly cron toggle) sits at the
// top of this panel unchanged.
//
// Phase 2 UI overhaul (2026-05-15): visual surfaces realigned with the
// player/agent arena-tinted glassmorphic look — hard borders → soft
// arena-tinted insetting rings, neutral palette → arena/neon tokens,
// fuchsia accent → violet (admin identity). All state, hooks, and API
// calls unchanged.

import { useEffect, useState } from 'react';
import { levelName } from '../../lib/levelNames';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { adminApi } from '@baaligames/shared-ui/admin/lib/adminApi';
import { INPUT_CLS } from '../../components/fieldStyles';
import { ApiError } from '@baaligames/shared-ui/lib/api';
import ConfirmPasswordModal from '@baaligames/shared-ui/admin/components/ConfirmPasswordModal';

// ── Phase R7 (2026-05-13): weekly auto-reassign toggle state ──
interface GeoReassignState {
  enabled: boolean;
  last_run_at: string | null;
  last_run_summary: null | {
    ran_at: string; mode: string;
    eligible: number; moved: number; unchanged: number; unmatched: number;
    by_level: Record<string, number>;
  };
  output_tail?: string;
  // Phase 7.5 follow-up redesign (2026-05-17): per-(kind, target_level)
  // last-run rows from the new geo_reassign_last_runs table. The new
  // top-down control center reads ran_at + eligible/moved/etc per
  // cell from this list.
  last_runs?: GeoLastRun[];
}

// One row of the per-cell last-run map. (kind, target_level) is the
// composite key; `summary` is the same JSON shape the cron script
// produces — surfaced here so the UI can show "X moved · Y unchanged"
// without an extra fetch.
interface GeoLastRun {
  kind: 'players' | 'agents';
  target_level: number;      // 0 = all (players only); 2..3 for agents; 1..3 for players
  ran_at: string;            // 'YYYY-MM-DD HH:MM:SS'
  mode: 'apply' | 'dry_run';
  duration_ms: number | null;
  eligible: number;
  moved: number;
  unchanged: number;
  unmatched: number;
  skipped_level_cap: number;
  summary: any;              // full summary JSON from the cron (includes blocked[])
}

// 2026-05-17 (safety overhaul): one row of the "currently blocked"
// audit table. Same shape returned by BOTH the cron's blocked-list
// emission (summary.blocked) AND the live /geo-reassign/blocked
// endpoint. Sorted ASCENDING by pending_since (oldest stuck first).
interface BlockedEntity {
  kind: 'player' | 'agent';
  reason: 'pending_recharge' | 'pending_withdrawal';
  id: number;
  username: string;
  first_name: string | null;
  last_name:  string | null;
  phone: string | null;
  avatar_url: string | null;
  level?: number | null;     // agents only
  area_name: string | null;
  pincode: string | null;
  current_parent_id: number | null;
  current_parent_username: string | null;
  wallet_balance: number;
  deposit_balance?: number;  // agents only
  pending_since: string;
}

interface BlockedResponse {
  players: BlockedEntity[];
  agents:  BlockedEntity[];
}

type Kind = 'players' | 'agents';

interface SourceRow {
  id: number;
  username: string;
  email?: string;
  phone?: string;
  uaid?: string | null;
  agent_id?: number | null;
  agent_username?: string | null;
  parent_agent_id?: number | null;
  parent_username?: string | null;
  level?: number;
  area_id: number | null;
  area_name: string | null;
  pincode_id: number | null;
  pincode: string | null;
  eligible: boolean;
  ineligibility_reasons: string[];
}

interface DestAgent {
  id: number;
  uaid: string | null;
  username: string;
  email: string | null;
  level: number;
  area_id: number | null;
  area_name: string | null;
  pincode_id: number | null;
  pincode: string | null;
  owned_players: number;
  owned_subagents: number;
}

export default function ReassignPanel() {
  const [kind, setKind] = useState<Kind>('players');

  // Source filters
  const [filterQ, setFilterQ]                   = useState('');
  const [filterPincode, setFilterPincode]       = useState('');
  const [parentPick, setParentPick]             = useState<DestAgent | null>(null);
  const [includeIneligible, setIncludeIneligible] = useState(true);

  const [rows, setRows]         = useState<SourceRow[] | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // Destination picker
  const [destPincode, setDestPincode]       = useState('');
  const [destPick, setDestPick]             = useState<DestAgent | null>(null);

  const [reason, setReason]         = useState('');
  const [busy, setBusy]             = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [err, setErr]               = useState<string | null>(null);
  const [toast, setToast]           = useState<string | null>(null);

  // Reset state when switching mode
  useEffect(() => {
    setRows(null); setSelected(new Set()); setDestPick(null); setParentPick(null);
    setFilterQ(''); setFilterPincode(''); setDestPincode(''); setReason('');
    setErr(null); setToast(null);
  }, [kind]);

  async function runSearch() {
    setBusy(true); setErr(null); setRows(null); setSelected(new Set());
    try {
      const qs = new URLSearchParams();
      if (filterQ.trim())       qs.set('q', filterQ.trim());
      if (filterPincode.trim()) qs.set('pincode', filterPincode.trim());
      if (parentPick?.id)       qs.set(kind === 'players' ? 'current_agent_id' : 'current_parent_id', String(parentPick.id));
      qs.set('include_ineligible', includeIneligible ? '1' : '0');
      qs.set('limit', '200');
      const path = kind === 'players' ? '/admin/bulk-reassign/players/search' : '/admin/bulk-reassign/agents/search';
      const r = await adminApi.get<{ rows: SourceRow[]; count: number }>(`${path}?${qs.toString()}`);
      setRows(r.rows);
    } catch (e) { setErr((e as ApiError).message); }
    finally { setBusy(false); }
  }

  function toggleRow(id: number) {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  }
  function selectAllEligible() {
    if (!rows) return;
    setSelected(new Set(rows.filter(r => r.eligible).map(r => r.id)));
  }
  function clearSelection() { setSelected(new Set()); }

  const eligibleCount   = rows ? rows.filter(r => r.eligible).length : 0;
  const ineligibleCount = rows ? rows.length - eligibleCount : 0;
  const canApply = selected.size > 0 && reason.trim().length > 0 && destPick != null;

  async function doApply(token: string) {
    setBusy(true); setErr(null);
    try {
      const body: any = {
        kind,
        new_parent_agent_id: destPick!.id,
        reason: reason.trim(),
      };
      if (kind === 'players') body.user_ids = [...selected];
      else                    body.agent_ids = [...selected];
      const r = await adminApi.post<{
        kind: Kind; affected: number; requested: number;
        skipped: { id: number; reasons: string[] }[];
        new_parent_agent_id: number | null;
      }>('/admin/bulk-reassign', body, token);
      const skipMsg = r.skipped.length
        ? ` Skipped ${r.skipped.length} (${r.skipped.map(s => `#${s.id}:${s.reasons.join('+')}`).join(', ')}).`
        : '';
      setToast(
        `Moved ${r.affected} of ${r.requested} ${kind === 'players' ? 'player' : 'sub-agent'}${r.affected === 1 ? '' : 's'} to @${destPick!.username}.${skipMsg}`,
      );
      setConfirming(false);
      // Refresh the row list to reflect new agent assignments.
      await runSearch();
      setSelected(new Set()); setReason('');
    } catch (e) { setErr((e as ApiError).message); }
    finally { setBusy(false); }
  }

  return (
    <div className="space-y-4">
      <GeoAutoReassignCard />

      {err && <Banner tone="error">{err}</Banner>}
      {toast && <Banner tone="success">{toast}</Banner>}

      {/* ─── 1) Mode picker ─── */}
      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                  className="rounded-2xl p-5 space-y-4
                             bg-white dark:bg-arena-700/40
                             ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                             shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                             backdrop-blur">
        <div>
          <span className="block text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60 mb-2 font-semibold">
            What are you moving?
          </span>
          <div className="flex gap-2">
            <KindBtn active={kind === 'players'} onClick={() => setKind('players')} icon="person"       label="Players" />
            <KindBtn active={kind === 'agents'}  onClick={() => setKind('agents')}  icon="account_tree" label="Sub-agents" />
          </div>
        </div>

        {/* ─── 2) Filters ─── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Search (username · email · UAID · phone)" hint="Free-text substring match.">
            <input value={filterQ} onChange={(e) => setFilterQ(e.target.value)}
                   placeholder="e.g. aman, gupta, 9876, ABCD1234"
                   className={inputCls} />
          </Field>
          <Field label="Pincode" hint="6-digit India pincode. Matches the player's / agent's own pincode_id.">
            <input value={filterPincode} onChange={(e) => setFilterPincode(e.target.value.replace(/[^0-9]/g, ''))}
                   placeholder="e.g. 482020"
                   className={inputCls + ' tabular-nums'} />
          </Field>
          <Field label={kind === 'players' ? 'Currently under agent (optional)' : 'Currently under parent agent (optional)'}
                 hint="Type to search; pick from the dropdown. Leave empty for any.">
            <AgentTypeahead value={parentPick} onChange={setParentPick} kind={kind} />
          </Field>
          <Field label="Show ineligible rows too?"
                 hint="Ineligible = pending recharge OR withdrawal. Shown disabled so you can see why they can't be moved.">
            <label className="inline-flex items-center gap-2 text-[12.5px] mt-2">
              <input type="checkbox" checked={includeIneligible} onChange={(e) => setIncludeIneligible(e.target.checked)} />
              Include ineligible
            </label>
          </Field>
        </div>

        <div className="flex justify-end">
          <button onClick={runSearch} disabled={busy}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full
                             bg-violet-600 hover:bg-violet-500 active:bg-violet-700
                             text-white text-[12.5px] font-bold
                             shadow-[0_2px_10px_rgba(139,92,246,0.4)]
                             transition active:scale-95 disabled:opacity-50">
            {busy ? <Icon name="progress_activity" size={13} className="animate-spin" /> : <Icon name="search" size={13} />}
            Search
          </button>
        </div>
      </motion.div>

      {/* ─── 3) Results ─── */}
      {rows && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                    className="rounded-2xl overflow-hidden
                               bg-white dark:bg-arena-700/40
                               ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                               shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                               backdrop-blur">
          <div className="px-4 py-3 border-b border-arena-200/40 dark:border-arena-600/40
                          bg-arena-50/60 dark:bg-arena-800/40
                          flex flex-wrap items-center gap-3 justify-between">
            <div>
              <div className="text-[13px] font-bold text-arena-800 dark:text-neon-200/90">
                {rows.length} result{rows.length === 1 ? '' : 's'} · {selected.size} selected
              </div>
              <div className="text-[11px] text-arena-600 dark:text-neon-200/60">
                {eligibleCount} eligible · {ineligibleCount} blocked by pending requests
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={selectAllEligible} disabled={eligibleCount === 0}
                      className="text-[11.5px] px-3 py-1 rounded-full
                                 bg-emerald-50 dark:bg-emerald-500/15
                                 text-emerald-800 dark:text-emerald-200
                                 ring-1 ring-inset ring-emerald-200/60 dark:ring-emerald-500/40
                                 hover:bg-emerald-100 disabled:opacity-50 transition">
                Select all {eligibleCount} eligible
              </button>
              <button onClick={clearSelection} disabled={selected.size === 0}
                      className="text-[11.5px] px-3 py-1 rounded-full
                                 bg-arena-100/60 dark:bg-arena-700/50
                                 text-arena-700 dark:text-neon-200/80
                                 ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40
                                 hover:bg-arena-200/60 dark:hover:bg-arena-700/70 disabled:opacity-50 transition">
                Clear
              </button>
            </div>
          </div>

          {rows.length === 0
            ? <div className="px-4 py-8 text-center text-[12.5px] text-arena-600 dark:text-neon-200/60">
                No rows match your filters.
              </div>
            : <table className="w-full text-[12.5px]">
                <thead className="bg-arena-50/40 dark:bg-arena-800/30
                                  text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60
                                  border-b border-arena-200/40 dark:border-arena-600/40">
                  <tr>
                    <th className="text-left py-3 px-4 w-8"></th>
                    <th className="text-left py-3 px-4">ID</th>
                    <th className="text-left py-3 px-4">{kind === 'players' ? 'Player' : 'Sub-agent'}</th>
                    <th className="text-left py-3 px-4">Location</th>
                    <th className="text-left py-3 px-4">Currently under</th>
                    <th className="text-left py-3 px-4">Eligibility</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-arena-200/40 dark:divide-arena-700/40">
                  {rows.map((r) => {
                    const isSel = selected.has(r.id);
                    return (
                      <tr key={r.id}
                          className={`hover:bg-arena-50/60 dark:hover:bg-white/[0.03] transition ${
                            r.eligible ? '' : 'bg-arena-50/40 dark:bg-arena-800/20 opacity-70'
                          }`}>
                        <td className="py-3 px-4">
                          <input type="checkbox" checked={isSel} disabled={!r.eligible}
                                 onChange={() => toggleRow(r.id)}
                                 className="accent-violet-600" />
                        </td>
                        <td className="py-3 px-4 font-mono text-[11px] text-arena-700 dark:text-neon-200/75">#{r.id}</td>
                        <td className="py-3 px-4">
                          <div className="font-semibold text-arena-900 dark:text-white">@{r.username}</div>
                          {r.uaid && <div className="font-mono text-[10.5px] text-arena-500 dark:text-neon-200/50">{r.uaid}</div>}
                        </td>
                        <td className="py-3 px-4 text-[11.5px] text-arena-700 dark:text-neon-200/75">
                          {r.area_name || <span className="text-arena-400 dark:text-neon-200/70">—</span>}
                          {r.pincode && <span className="text-arena-600 dark:text-neon-200/60 ml-1">· {r.pincode}</span>}
                        </td>
                        <td className="py-3 px-4 text-[11.5px] text-arena-700 dark:text-neon-200/75">
                          {kind === 'players'
                            ? (r.agent_username ? `@${r.agent_username}` : <span className="text-arena-400 dark:text-neon-200/70">admin-direct</span>)
                            : (r.parent_username ? `@${r.parent_username} · ${levelName(r.level)}` : <span className="text-arena-400 dark:text-neon-200/70">admin-direct</span>)}
                        </td>
                        <td className="py-3 px-4">
                          {r.eligible
                            ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold
                                                bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-200
                                                ring-1 ring-inset ring-emerald-200/60 dark:ring-emerald-500/40">
                                <Icon name="check_circle" size={11} /> eligible
                              </span>
                            : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold
                                                bg-amber-50 dark:bg-amber-500/15 text-amber-800 dark:text-amber-200
                                                ring-1 ring-inset ring-amber-200/60 dark:ring-amber-500/40"
                                    title={r.ineligibility_reasons.join(', ')}>
                                <Icon name="block" size={11} />
                                {r.ineligibility_reasons.map(r => r.replace('pending_', '')).join(' + ')}
                              </span>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>}
        </motion.div>
      )}

      {/* ─── 4) Destination + 5) Reason + 6) Apply ─── */}
      {rows && rows.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                    className="rounded-2xl p-5 space-y-4
                               bg-white dark:bg-arena-700/40
                               ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                               shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                               backdrop-blur">
          <Field label={`Destination agent (will receive the ${kind === 'players' ? 'players' : 'sub-agents'})`}
                 hint={`Only agents who have turned ON "Accepting ${kind === 'players' ? 'players' : 'sub-agents'}" appear here.`}>
            <div className="space-y-2">
              <div className="flex gap-2">
                <input value={destPincode}
                       onChange={(e) => setDestPincode(e.target.value.replace(/[^0-9]/g, ''))}
                       placeholder="Filter destinations by pincode (optional)"
                       className={inputCls + ' tabular-nums max-w-xs'} />
              </div>
              <AgentTypeahead value={destPick} onChange={setDestPick} kind={kind}
                              acceptKind={kind} pincode={destPincode || undefined} />
              {destPick && (
                <div className="text-[11.5px] text-emerald-700 dark:text-emerald-300 flex items-center gap-1">
                  <Icon name="check_circle" size={12} /> Sending to <b>@{destPick.username}</b>
                  {destPick.area_name && <span className="text-arena-600 dark:text-neon-200/60"> · {destPick.area_name}</span>}
                  {destPick.pincode && <span className="text-arena-600 dark:text-neon-200/60"> · {destPick.pincode}</span>}
                  <span className="text-arena-600 dark:text-neon-200/60"> · already owns {destPick.owned_players} players / {destPick.owned_subagents} sub-agents</span>
                </div>
              )}
            </div>
          </Field>

          <Field label="Reason (required — written to audit log)"
                 hint="Visible to anyone reviewing the bulk_reassign event later.">
            <textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)}
                      placeholder="e.g. Resigned agent X — redistributing their downline to Y."
                      className={inputCls + ' resize-y'} />
          </Field>

          <div className="flex flex-wrap gap-2 items-center justify-end">
            <span className="text-[11.5px] text-arena-600 dark:text-neon-200/60 mr-auto">
              {selected.size} selected · → {destPick ? `@${destPick.username}` : 'pick a destination'}
            </span>
            <button onClick={() => setConfirming(true)} disabled={!canApply || busy}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full
                               bg-violet-600 hover:bg-violet-500 active:bg-violet-700
                               text-white text-[12.5px] font-bold
                               shadow-[0_2px_10px_rgba(139,92,246,0.4)]
                               transition active:scale-95 disabled:opacity-50">
              <Icon name="swap_horiz" size={13} /> Apply reassignment
            </button>
          </div>
        </motion.div>
      )}

      {confirming && destPick && (
        <ConfirmPasswordModal
          title={`Reassign ${selected.size} ${kind === 'players' ? 'player' : 'sub-agent'}${selected.size === 1 ? '' : 's'} to @${destPick.username}?`}
          subtitle={`Reason: "${reason.trim()}". Server skips any row with a pending recharge/withdrawal.`}
          dangerLabel="Confirm reassign"
          tone="fuchsia"
          onCancel={() => setConfirming(false)}
          onConfirmed={doApply}
        />
      )}
    </div>
  );
}

// ─── Geo-Reassign Control Center ──────────────────────────────────
// 2026-05-17 (Phase 7.5 follow-up redesign):
// Top-down, level-by-level control. Instead of one big "Run now"
// button that moves every eligible row at once, the admin runs each
// hierarchy level deliberately:
//
//   Sub-agents (3-level hierarchy, migration 132):
//     Step 1: L2 (Masters) → verify in dashboard → Step 2: L3 (Agents)
//
//   Players:
//     "All players" or filter by current agent's level so the admin
//     can fix-up the players who were under a particular tier.
//
// Each cell shows its own status (Never run · Last run timestamp ·
// Last result chip) so the operator has visual proof before moving
// to the next step. Backed by the geo_reassign_last_runs table
// (migration 082) so timestamps survive page reloads.
//
// The master enable toggle gates the entire system — when OFF, every
// per-cell Run button shows a tooltip and is disabled. The "Run now"
// path bypasses the toggle via ?force=1 only if the cell explicitly
// opts in (we don't for safety — admin should flip the master ON
// before any per-cell run).

interface GeoCell {
  // The composite key the backend uses for upsert.
  kind: 'players' | 'agents';
  targetLevel: number;        // 0=all (players), 1..3=level filter, 2..3 for agents
  // Display.
  title: string;              // "Masters (Level 2)"
  subtitle: string;           // one-line plain-English purpose
  step?: number;              // for top-down numbered steps
}

// The 6 cells the control center renders (3-level hierarchy:
// Super Master L1 / Master L2 / Agent L3 — migration 132).
//   Sub-agents: L2..L3 (2 cells) — top-down sequence.
//   Players:    All + 3 level filters (4 cells).
const AGENT_CELLS: GeoCell[] = [
  { kind: 'agents', targetLevel: 2, step: 1, title: 'Masters (Level 2)', subtitle: 'Top of the chain after admin-direct Super Masters. Run this first.' },
  { kind: 'agents', targetLevel: 3, step: 2, title: 'Agents (Level 3)',  subtitle: 'After Masters are settled. Each Agent finds its nearest (now-relocated) Master.' },
];

const PLAYER_CELLS: GeoCell[] = [
  { kind: 'players', targetLevel: 0, title: 'All players',                      subtitle: 'After every agent tier is stabilized, run this once to attach players to their nearest agent regardless of current tier.' },
  { kind: 'players', targetLevel: 1, title: 'Players under Super Masters (L1)', subtitle: 'Only players whose current agent is a Super Master (admin-direct).' },
  { kind: 'players', targetLevel: 2, title: 'Players under Masters (L2)',       subtitle: 'Only players whose current agent is a Master.' },
  { kind: 'players', targetLevel: 3, title: 'Players under Agents (L3)',        subtitle: 'Only players whose current agent is an Agent.' },
];

function GeoAutoReassignCard() {
  const [state, setState] = useState<GeoReassignState | null>(null);
  const [blocked, setBlocked] = useState<BlockedResponse | null>(null);
  const [busy, setBusy] = useState(false);
  const [busyCellKey, setBusyCellKey] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  // 2026-05-17 (safety overhaul): the conditional override checkbox.
  // pending RECHARGE is ALWAYS blocked (never UI-visible to override);
  // pending WITHDRAWAL admin can flip via this toggle.
  const [allowPendingWithdrawal, setAllowPendingWithdrawal] = useState(false);
  // 2026-05-17 (post-run modal): when a Run finishes, capture which
  // cell was run + the just-fetched blocked list so the modal can
  // pop up immediately with "you just ran X — here's what was
  // blocked." Closing the modal sets this back to null; the inline
  // blocked section below remains populated regardless (the modal
  // is a transient ALERT, not the source of truth).
  const [postRunReport, setPostRunReport] = useState<{
    cell: GeoCell;
    lastRun: GeoLastRun | null;
    blocked: BlockedResponse;
  } | null>(null);

  async function refresh() {
    setErr(null);
    try {
      const [s, b] = await Promise.all([
        adminApi.get<GeoReassignState>('/admin/system/geo-reassign'),
        adminApi.get<BlockedResponse>('/admin/system/geo-reassign/blocked'),
      ]);
      setState(s);
      setBlocked(b);
    } catch (e) { setErr((e as ApiError).message); }
  }
  useEffect(() => { refresh(); }, []);

  async function toggle(next: boolean) {
    setBusy(true); setErr(null); setToast(null);
    try {
      await adminApi.patch<GeoReassignState>('/admin/system/geo-reassign', { enabled: next });
      // The PATCH endpoint returns the legacy shape only — re-fetch
      // to grab the last_runs[] map alongside the new enabled state.
      await refresh();
      setToast(next ? 'Manual reassign turned ON — per-cell runs allowed.' : 'Manual reassign turned OFF — all runs blocked.');
    } catch (e) { setErr((e as ApiError).message); }
    finally { setBusy(false); }
  }

  async function runCell(cell: GeoCell) {
    const key = `${cell.kind}-${cell.targetLevel}`;
    setBusy(true); setBusyCellKey(key); setErr(null); setToast(null);
    try {
      const s = await adminApi.post<GeoReassignState>('/admin/system/geo-reassign/run-now', {
        kind: cell.kind,
        targetLevel: cell.targetLevel,
        allowPendingWithdrawal,    // 2026-05-17: conditional pending-withdrawal override
      });
      setState(s);
      // After the run, ALSO refresh the live blocked list so the
      // audit table reflects whatever pending requests still remain.
      const b = await adminApi.get<BlockedResponse>('/admin/system/geo-reassign/blocked');
      setBlocked(b);
      // Surface this cell's stats in the toast (kept for the
      // "everything OK" cases where no modal pops up).
      const lr = (s.last_runs || []).find(r => r.kind === cell.kind && r.target_level === cell.targetLevel) || null;
      if (lr) {
        setToast(`${cell.title}: ${lr.eligible} eligible · ${lr.moved} moved · ${lr.unchanged} unchanged · ${lr.unmatched} unmatched.`);
      } else {
        setToast(`${cell.title}: run complete.`);
      }
      // 2026-05-17 (post-run modal): instant alert. Pop the modal
      // open with the just-completed run's summary AND the live
      // blocked list, so admin sees "here's what didn't move and
      // why" without having to scroll down. Close button on the
      // modal just sets this null — the inline blocked section
      // below has the same data already; nothing reloads.
      setPostRunReport({ cell, lastRun: lr, blocked: b });
    } catch (e) { setErr((e as ApiError).message); }
    finally { setBusy(false); setBusyCellKey(null); }
  }

  const enabled = !!state?.enabled;
  const lastRunsByKey = new Map<string, GeoLastRun>();
  for (const lr of state?.last_runs || []) {
    lastRunsByKey.set(`${lr.kind}-${lr.target_level}`, lr);
  }

  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl p-5 space-y-4
                           bg-white dark:bg-arena-700/40
                           ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                           shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none
                           backdrop-blur">
      {/* Header + master enable toggle */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Icon name="travel_explore" size={18} className="text-violet-600 dark:text-violet-300" />
            <h3 className="text-[14px] font-bold text-arena-900 dark:text-white">
              Geo-reassign control center
            </h3>
            <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9.5px] font-bold uppercase tracking-wider
                             bg-rose-50 dark:bg-rose-500/15 text-rose-700 dark:text-rose-200
                             ring-1 ring-inset ring-rose-200/60 dark:ring-rose-500/40">
              <Icon name="lock" size={10} /> Manual only
            </span>
          </div>
          <p className="text-[11.5px] text-arena-600 dark:text-neon-200/60 mt-1 leading-relaxed max-w-2xl">
            100% manual operation. There is <b>NO automated cron</b> for this tool — every reassignment happens only
            when you explicitly click a Run button below. Top-down sequencing recommended: stabilize each agent
            tier (Masters, then Agents) before touching players.
          </p>
        </div>
        <button onClick={() => toggle(!enabled)} disabled={busy || !state}
                title={enabled ? 'Click to disable all geo-reassign runs' : 'Click to enable geo-reassign'}
                className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition
                           ${enabled ? 'bg-emerald-500' : 'bg-arena-300 dark:bg-arena-700'}
                           disabled:opacity-50`}>
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition
                            ${enabled ? 'translate-x-6' : 'translate-x-1'}`} />
        </button>
      </div>

      {err && <Banner tone="error">{err}</Banner>}
      {toast && <Banner tone="success">{toast}</Banner>}

      {/* Master status chip */}
      <div className="rounded-xl px-3 py-2 bg-arena-50/60 dark:bg-arena-800/40
                      ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                      flex items-center gap-2 text-[12px]">
        <span className={`inline-flex w-2 h-2 rounded-full ${enabled ? 'bg-emerald-500' : 'bg-arena-400 dark:bg-arena-600'}`} />
        <span className="text-arena-900 dark:text-white font-bold">
          {enabled ? 'Master toggle is ON — per-cell runs are allowed' : 'Master toggle is OFF — turn ON above to run any cell'}
        </span>
      </div>

      {/* ────── Eligibility rules panel (always visible) ────── */}
      <EligibilityRulesPanel />

      {/* ────── Pending-withdrawal conditional override checkbox ────── */}
      <div className="rounded-xl px-3 py-2.5 bg-amber-50/40 dark:bg-amber-500/[0.07]
                      ring-1 ring-inset ring-amber-200/50 dark:ring-amber-500/30">
        <label className="flex items-start gap-2 cursor-pointer">
          <input type="checkbox"
                 checked={allowPendingWithdrawal}
                 onChange={(e) => setAllowPendingWithdrawal(e.target.checked)}
                 disabled={busy}
                 className="mt-0.5 accent-violet-600" />
          <div className="min-w-0">
            <div className="text-[12px] font-bold text-arena-900 dark:text-white">
              Allow reassignment even if they have pending withdrawal requests
            </div>
            <p className="text-[10.5px] text-arena-700 dark:text-neon-200/65 leading-snug mt-0.5">
              Default: <b>OFF</b> (skipped — gives the current parent a chance to pay). Tick this only if you've
              decided the existing parent is unreliable and the child needs a new one immediately. Pending
              <b> recharge</b> requests remain ALWAYS blocked — those represent money already in transit to the
              current parent and can NEVER be overridden.
            </p>
          </div>
        </label>
      </div>

      {/* ────── Sub-agents section (top-down) ────── */}
      <SectionHeader
        icon="account_tree"
        title="Sub-agents — top-down sequence"
        subtitle="Run Masters (L2) first, verify, then Agents (L3). Each move respects the cycle gate and the 3-level depth cap."
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {AGENT_CELLS.map(cell => (
          <GeoCellCard key={`${cell.kind}-${cell.targetLevel}`}
                       cell={cell}
                       lastRun={lastRunsByKey.get(`${cell.kind}-${cell.targetLevel}`) || null}
                       disabled={!enabled || busy}
                       isRunning={busyCellKey === `${cell.kind}-${cell.targetLevel}`}
                       onRun={() => runCell(cell)} />
        ))}
      </div>

      {/* ────── Players section ────── */}
      <SectionHeader
        icon="person"
        title="Players"
        subtitle="After every sub-agent tier is stable, run the player rows. The level filter is for sequential fix-ups (e.g. 'just refresh players whose agent is L2 now')."
      />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {PLAYER_CELLS.map(cell => (
          <GeoCellCard key={`${cell.kind}-${cell.targetLevel}`}
                       cell={cell}
                       lastRun={lastRunsByKey.get(`${cell.kind}-${cell.targetLevel}`) || null}
                       disabled={!enabled || busy}
                       isRunning={busyCellKey === `${cell.kind}-${cell.targetLevel}`}
                       onRun={() => runCell(cell)} />
        ))}
      </div>

      {/* ────── Currently blocked audit table ────── */}
      <BlockedEntitiesAudit blocked={blocked} allowPendingWithdrawal={allowPendingWithdrawal} />

      {/* 2026-05-17 — Post-run modal. Auto-opens when a cell finishes
          running. Renders the same BlockedTable layout as the inline
          section, but inside an overlaid modal so admin sees results
          instantly without scrolling. Close dismisses the modal only
          — inline state remains. */}
      <AnimatePresence>
        {postRunReport && (
          <PostRunModal report={postRunReport}
                        allowPendingWithdrawal={allowPendingWithdrawal}
                        onClose={() => setPostRunReport(null)} />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/** Post-run modal: bottom-sheet on mobile, centered dialog on
 *  desktop. Reuses the same BlockedTable component the inline
 *  section uses — single source of presentational truth, so any
 *  future tweak to the row layout propagates to both surfaces. */
function PostRunModal({ report, allowPendingWithdrawal, onClose }: {
  report: { cell: GeoCell; lastRun: GeoLastRun | null; blocked: BlockedResponse };
  allowPendingWithdrawal: boolean;
  onClose: () => void;
}) {
  const { cell, lastRun, blocked } = report;
  // Pick which blocked list to show in the modal: agents OR players,
  // matching the cell that was just run. The other kind's blocked
  // entities are visible in the inline section below — not duplicated.
  const relevantBlocked = cell.kind === 'agents' ? blocked.agents : blocked.players;
  const noun = cell.kind === 'agents' ? 'Sub-agents' : 'Players';

  // Esc to close — standard modal affordance.
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // 2026-05-17: portal to document.body. The card that holds this
  // panel uses `<motion.div>` for its mount animation, which applies
  // a `transform` style to its root element. CSS spec: a transformed
  // ancestor becomes the containing block for any `position: fixed`
  // descendant — so without the portal, this modal renders relative
  // to the card (which is ~2800px down the page) instead of relative
  // to the viewport. Portaling to body sidesteps the transformed
  // ancestor entirely.
  return createPortal(
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.18 }}
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm
                 flex items-end sm:items-start sm:items-center justify-center
                 p-0 sm:p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
        transition={{ type: 'spring', damping: 28, stiffness: 320 }}
        className="w-full sm:max-w-3xl bg-white dark:bg-arena-800
                   rounded-t-2xl sm:rounded-2xl
                   ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40
                   shadow-2xl
                   max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Drag handle (mobile bottom-sheet hint) */}
        <div className="sm:hidden flex justify-center pt-2 pb-1">
          <span className="w-10 h-1 rounded-full bg-arena-300 dark:bg-arena-600" />
        </div>

        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-4 sm:px-5 py-3
                        border-b border-arena-200/40 dark:border-arena-700/40">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Icon name="check_circle" size={16} className="text-emerald-600 dark:text-emerald-300" />
              <h3 className="text-[13.5px] sm:text-[14.5px] font-bold text-arena-900 dark:text-white truncate">
                Run complete — {cell.title}
              </h3>
            </div>
            {lastRun && (
              <div className="text-[11px] text-arena-600 dark:text-neon-200/60 mt-0.5 font-mono tabular-nums">
                {lastRun.ran_at}
                {lastRun.duration_ms != null && <span> · {lastRun.duration_ms} ms</span>}
              </div>
            )}
          </div>
          <button onClick={onClose}
                  className="shrink-0 grid place-items-center w-8 h-8 rounded-full
                             hover:bg-arena-100 dark:hover:bg-arena-700
                             text-arena-700 dark:text-neon-200/70 transition"
                  aria-label="Close">
            <Icon name="close" size={18} />
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="overflow-y-auto px-4 sm:px-5 py-4 space-y-4">
          {/* Run stats */}
          {lastRun && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <StatBlock label="Eligible"  value={lastRun.eligible}  />
              <StatBlock label="Moved"     value={lastRun.moved}     tone={lastRun.moved > 0 ? 'emerald' : undefined} />
              <StatBlock label="Unchanged" value={lastRun.unchanged} />
              <StatBlock label="Unmatched" value={lastRun.unmatched} tone={lastRun.unmatched > 0 ? 'amber' : undefined} />
            </div>
          )}

          {/* The blocked list — reuses the same BlockedTable
              component the inline section uses. Stacked-card on
              mobile, real table on desktop. */}
          {relevantBlocked.length === 0
            ? <div className="rounded-xl px-3 py-4 text-center
                              bg-emerald-50/60 dark:bg-emerald-500/10
                              ring-1 ring-inset ring-emerald-200/50 dark:ring-emerald-500/30
                              text-[12.5px] text-emerald-800 dark:text-emerald-100 font-semibold">
                ✓ No {noun.toLowerCase()} are currently blocked by pending requests.
              </div>
            : <div className="space-y-2">
                <div className="text-[11.5px] text-arena-700 dark:text-neon-200/80 leading-relaxed">
                  <b>{relevantBlocked.length}</b> {noun.toLowerCase()} {relevantBlocked.length === 1 ? 'is' : 'are'} currently held back by pending requests.
                  These weren't moved during this run. Sorted oldest-first — address the top row first.
                </div>
                <BlockedTable rows={relevantBlocked}
                              kind={cell.kind === 'agents' ? 'agent' : 'player'}
                              allowPendingWithdrawal={allowPendingWithdrawal} />
              </div>}

          {/* Footnote: close-behaviour explainer so admin isn't
              afraid the data disappears on close. */}
          <p className="text-[10.5px] text-arena-500 dark:text-neon-200/45 italic leading-relaxed">
            Closing this dialog just dismisses the alert — the same blocked list remains visible in the
            "Currently blocked" section below until the pending requests are resolved.
          </p>
        </div>

        {/* Footer — close action */}
        <div className="border-t border-arena-200/40 dark:border-arena-700/40
                        px-4 sm:px-5 py-3 flex justify-end">
          <button onClick={onClose}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full
                             bg-violet-600 hover:bg-violet-500 active:bg-violet-700
                             text-white text-[12.5px] font-bold
                             shadow-[0_2px_10px_rgba(139,92,246,0.4)]
                             transition active:scale-95">
            <Icon name="check" size={13} /> Got it
          </button>
        </div>
      </motion.div>
    </motion.div>,
    document.body,
  );
}

/** Stat block in the post-run modal header — same rhythm as
 *  ModalStatsGrid used elsewhere in admin, kept inline here so we
 *  don't drag in a cross-file dependency. */
function StatBlock({ label, value, tone }: { label: string; value: number; tone?: 'emerald' | 'amber' }) {
  const color = tone === 'emerald' ? 'text-emerald-700 dark:text-emerald-300'
              : tone === 'amber'   ? 'text-amber-700 dark:text-amber-300'
              : 'text-arena-900 dark:text-white';
  return (
    <div className="rounded-xl px-3 py-2.5
                    bg-arena-50/60 dark:bg-arena-700/40
                    ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40">
      <div className="text-[10px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60 font-bold">{label}</div>
      <div className={`text-[18px] font-display font-extrabold tabular-nums ${color}`}>{value}</div>
    </div>
  );
}

/** The full eligibility + ideal-parent rules block, printed
 *  prominently so the admin always sees exactly what conditions
 *  the script is checking. Mirrors the rules audited from
 *  cron_weekly_geo_reassign.mjs + geographicMatch.js. */
function EligibilityRulesPanel() {
  return (
    <div className="rounded-xl px-4 py-3
                    bg-violet-50/50 dark:bg-violet-500/[0.07]
                    ring-1 ring-inset ring-violet-200/60 dark:ring-violet-500/30 space-y-3">
      <div className="flex items-center gap-2">
        <Icon name="rule" size={14} className="text-violet-700 dark:text-violet-200" />
        <h4 className="text-[12.5px] font-bold text-violet-900 dark:text-violet-100">
          Eligibility rules — exactly what the script checks
        </h4>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-[11px]">
        <div>
          <div className="text-[10.5px] uppercase tracking-wider font-bold text-violet-800 dark:text-violet-200 mb-1">
            Player / sub-agent eligibility
          </div>
          <ul className="space-y-0.5 list-disc list-inside text-arena-800 dark:text-neon-200/80">
            <li><b>status = 'active'</b></li>
            <li>All 6 geo fields populated (latitude, longitude, state_id, district_id, area_id, pincode_id)</li>
            <li>For sub-agents: <b>parent_agent_id IS NOT NULL</b> (L1 cannot be moved)</li>
            <li><span className="text-rose-700 dark:text-rose-300 font-semibold">Pending recharge → ABSOLUTE BLOCK (never overrideable)</span></li>
            <li><span className="text-amber-700 dark:text-amber-300 font-semibold">Pending withdrawal → conditional (default block; admin toggle above)</span></li>
          </ul>
        </div>
        <div>
          <div className="text-[10.5px] uppercase tracking-wider font-bold text-violet-800 dark:text-violet-200 mb-1">
            Ideal parent — destination criteria
          </div>
          <ul className="space-y-0.5 list-disc list-inside text-arena-800 dark:text-neon-200/80">
            <li><b>status = 'active'</b></li>
            <li>Has GPS lat/lng populated</li>
            <li>For players: <b>is_accepting_players = 1</b> AND <b>deposit_balance &gt; 0</b></li>
            <li>For sub-agents: <b>is_accepting_agents = 1</b> AND NOT self, NOT in self's descendants (cycle)</li>
            <li>Distance cascade: <b>20 km Haversine</b> → <b>100 km</b> → area → district → state → nation</li>
            <li>Tie-break: deposit DESC, current load ASC, id ASC</li>
            <li>For sub-agents: new_level = candidate.level + 1, and (new_level + deepest-descendant hops) ≤ 3</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

/** The "currently blocked from reassignment" audit table. Two
 *  separate tables (Players / Sub-agents) because sub-agents have
 *  the extra Deposit Balance + Level columns. Sorted ascending by
 *  pending_since (oldest stuck at the top — admin's priority queue). */
function BlockedEntitiesAudit({ blocked, allowPendingWithdrawal }: {
  blocked: BlockedResponse | null;
  allowPendingWithdrawal: boolean;
}) {
  if (!blocked) {
    return (
      <div className="rounded-xl px-3 py-3 bg-arena-50/60 dark:bg-arena-800/40
                      ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                      text-[11.5px] text-arena-600 dark:text-neon-200/60">
        Loading currently-blocked entities…
      </div>
    );
  }

  const playerCount = blocked.players.length;
  const agentCount  = blocked.agents.length;
  if (playerCount === 0 && agentCount === 0) {
    return (
      <div className="rounded-xl px-3 py-3 bg-emerald-50/60 dark:bg-emerald-500/10
                      ring-1 ring-inset ring-emerald-200/50 dark:ring-emerald-500/30
                      flex items-center gap-2 text-[12px]">
        <Icon name="check_circle" size={14} className="text-emerald-700 dark:text-emerald-300" />
        <span className="text-emerald-900 dark:text-emerald-100 font-semibold">
          No entities are currently blocked by pending requests. Everyone is movable.
        </span>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <SectionHeader
        icon="block"
        title="Currently blocked — manual intervention queue"
        subtitle={`Sorted oldest-first. ${playerCount} player${playerCount === 1 ? '' : 's'} + ${agentCount} sub-agent${agentCount === 1 ? '' : 's'} currently held back by pending requests. Each row shows the EXACT reason. Address the oldest first.`}
      />
      {agentCount > 0 && (
        <BlockedTable rows={blocked.agents} kind="agent"
                      allowPendingWithdrawal={allowPendingWithdrawal} />
      )}
      {playerCount > 0 && (
        <BlockedTable rows={blocked.players} kind="player"
                      allowPendingWithdrawal={allowPendingWithdrawal} />
      )}
    </div>
  );
}

function BlockedTable({ rows, kind, allowPendingWithdrawal }: {
  rows: BlockedEntity[];
  kind: 'player' | 'agent';
  allowPendingWithdrawal: boolean;
}) {
  const isAgent = kind === 'agent';
  // Compute "now" for the relative-time chip ("Stuck for X days").
  const now = Date.now();

  // 2026-05-17 (mobile redesign): the 10-column table overflows the
  // 390px iPhone viewport disastrously. We now render TWO complete
  // layouts inside this component:
  //   * `md:hidden`         → stacked-card list (mobile-first)
  //   * `hidden md:block`   → original table (desktop)
  // Both consume the same `rows` array and run the same derivation
  // logic; this just splits the presentational rendering. No data
  // path forks.

  // Shared per-row derivation — used by both layouts.
  function derive(r: BlockedEntity, idx: number) {
    const stuckMs = Math.max(0, now - Date.parse(r.pending_since.replace(' ', 'T') + 'Z'));
    const stuckDays = Math.floor(stuckMs / (24 * 3600_000));
    const stuckHours = Math.floor((stuckMs / 3600_000) % 24);
    const stuckLabel = stuckDays > 0 ? `${stuckDays}d ${stuckHours}h` : `${stuckHours}h`;
    const isOldest = idx === 0;
    const isOverridden = allowPendingWithdrawal && r.reason === 'pending_withdrawal';
    const fullName = isAgent
      ? `@${r.username}` + (r.level != null ? ` · ${levelName(r.level)}` : '')
      : [r.first_name, r.last_name].filter(Boolean).join(' ') || `@${r.username}`;
    return { stuckLabel, isOldest, isOverridden, fullName };
  }

  // Reason badge factored out so both layouts render identically.
  function ReasonBadge({ reason, isOverridden }: { reason: BlockedEntity['reason']; isOverridden: boolean }) {
    if (reason === 'pending_recharge') {
      return (
        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9.5px] font-bold uppercase tracking-wider
                         bg-rose-50 dark:bg-rose-500/15 text-rose-700 dark:text-rose-200
                         ring-1 ring-inset ring-rose-200/60 dark:ring-rose-500/30"
              title="Absolute block — child has already paid the current parent.">
          <Icon name="lock" size={10} /> Recharge
        </span>
      );
    }
    return (
      <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-md text-[9.5px] font-bold uppercase tracking-wider
                        ${isOverridden
                          ? 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-200 ring-emerald-200/60 dark:ring-emerald-500/30'
                          : 'bg-amber-50 dark:bg-amber-500/15 text-amber-800 dark:text-amber-200 ring-amber-200/60 dark:ring-amber-500/30'}
                        ring-1 ring-inset`}
            title={isOverridden ? 'Override is ON — this row is NOT blocked right now' : 'Conditional block — admin can override'}>
        <Icon name={isOverridden ? 'check' : 'pending'} size={10} /> Withdrawal{isOverridden ? ' · overridden' : ''}
      </span>
    );
  }

  return (
    <div className="rounded-xl overflow-hidden ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                    bg-white dark:bg-arena-700/40">
      <div className="px-3 py-2 text-[11px] font-bold uppercase tracking-wider
                      bg-arena-50/80 dark:bg-arena-800/60
                      text-arena-700 dark:text-neon-200/80
                      border-b border-arena-200/40 dark:border-arena-700/40">
        {isAgent ? 'Sub-agents' : 'Players'} blocked from reassignment · {rows.length} row{rows.length === 1 ? '' : 's'}
      </div>

      {/* ─── Mobile: stacked cards (< md) ─── */}
      <div className="md:hidden divide-y divide-arena-200/40 dark:divide-arena-700/40">
        {rows.map((r, idx) => {
          const { stuckLabel, isOldest, isOverridden, fullName } = derive(r, idx);
          return (
            <div key={`${r.kind}-${r.id}-${r.reason}-m`}
                 className={`px-3 py-3 space-y-2.5
                             ${isOldest ? 'bg-rose-50/40 dark:bg-rose-500/[0.08]' : ''}
                             ${isOverridden ? 'opacity-55' : ''}`}>
              {/* Identity row — avatar + name + ID + reason badge */}
              <div className="flex items-start gap-2.5">
                <BlockedAvatar url={r.avatar_url} username={r.username} />
                <div className="min-w-0 flex-1">
                  <div className={`text-[13px] font-bold leading-tight ${isOverridden ? 'line-through' : ''} text-arena-900 dark:text-white`}>
                    {fullName}
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    <span className="font-mono text-[10px] text-arena-500 dark:text-neon-200/50">#{r.id}</span>
                    {!isAgent && <span className="font-mono text-[10px] text-arena-500 dark:text-neon-200/50">@{r.username}</span>}
                    <ReasonBadge reason={r.reason} isOverridden={isOverridden} />
                  </div>
                </div>
              </div>

              {/* Contact + location key/value pairs */}
              <div className="grid grid-cols-1 gap-1 text-[11.5px]">
                <KV label="Phone"   value={r.phone || '—'} mono />
                <KV label="Location"
                    value={r.area_name
                      ? `${r.area_name}${r.pincode ? ` · ${r.pincode}` : ''}`
                      : '—'} />
                <KV label="Current parent"
                    value={r.current_parent_username
                      ? `@${r.current_parent_username}`
                      : 'admin-direct'} />
              </div>

              {/* Money row — wallet (+ deposit for agents) */}
              <div className="flex items-center gap-3 text-[11.5px] pt-1
                              border-t border-dashed border-arena-200/50 dark:border-arena-700/40">
                <div>
                  <div className="text-[9.5px] uppercase tracking-wider text-arena-500 dark:text-neon-200/50">Wallet</div>
                  <div className="font-mono tabular-nums font-bold text-arena-900 dark:text-white">
                    ₹{(r.wallet_balance || 0).toLocaleString('en-IN')}
                  </div>
                </div>
                {isAgent && (
                  <div>
                    <div className="text-[9.5px] uppercase tracking-wider text-arena-500 dark:text-neon-200/50">Deposit</div>
                    <div className="font-mono tabular-nums font-bold text-arena-900 dark:text-white">
                      ₹{((r as any).deposit_balance || 0).toLocaleString('en-IN')}
                    </div>
                  </div>
                )}
              </div>

              {/* Pending-since row — emphasised because it's the
                  sort key and the priority cue */}
              <div className={`rounded-lg px-2.5 py-1.5 text-[11px]
                              ${isOldest
                                ? 'bg-rose-100/70 dark:bg-rose-500/15 ring-1 ring-inset ring-rose-200/60 dark:ring-rose-500/30'
                                : 'bg-arena-50/80 dark:bg-arena-800/50 ring-1 ring-inset ring-arena-200/50 dark:ring-arena-700/40'}`}>
                <div className="text-[9.5px] uppercase tracking-wider font-bold
                                text-arena-600 dark:text-neon-200/60">
                  Pending since
                </div>
                <div className="font-mono tabular-nums text-arena-900 dark:text-white">{r.pending_since}</div>
                <div className={`text-[10.5px] font-bold mt-0.5
                                 ${isOldest ? 'text-rose-700 dark:text-rose-300' : 'text-arena-700 dark:text-neon-200/70'}`}>
                  stuck {stuckLabel}{isOldest ? ' ← oldest, address first' : ''}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ─── Desktop: original table (>= md) ─── */}
      <div className="hidden md:block overflow-auto">
        <table className="w-full text-[11.5px]">
          <thead className="text-[9.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60
                            border-b border-arena-200/40 dark:border-arena-700/40
                            bg-arena-50/40 dark:bg-arena-800/30">
            <tr>
              <th className="text-left px-2 py-2 w-10"></th>
              <th className="text-left px-2 py-2">ID</th>
              <th className="text-left px-2 py-2">{isAgent ? 'Sub-agent' : 'Player'}</th>
              <th className="text-left px-2 py-2">Phone</th>
              <th className="text-left px-2 py-2">Location</th>
              <th className="text-left px-2 py-2">Current parent</th>
              <th className="text-right px-2 py-2">Wallet</th>
              {isAgent && <th className="text-right px-2 py-2">Deposit</th>}
              <th className="text-left px-2 py-2">Pending since</th>
              <th className="text-left px-2 py-2">Reason</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-arena-200/40 dark:divide-arena-700/40">
            {rows.map((r, idx) => {
              const { stuckLabel, isOldest, isOverridden, fullName } = derive(r, idx);
              return (
                <tr key={`${r.kind}-${r.id}-${r.reason}`}
                    className={`hover:bg-arena-50/60 dark:hover:bg-white/[0.03] transition
                                ${isOldest ? 'bg-rose-50/30 dark:bg-rose-500/[0.08]' : ''}
                                ${isOverridden ? 'opacity-50' : ''}`}
                    title={isOverridden ? 'You have ticked the override — this row is NOT actually blocked at this moment' : ''}>
                  <td className="px-2 py-2">
                    <BlockedAvatar url={r.avatar_url} username={r.username} />
                  </td>
                  <td className="px-2 py-2 font-mono text-[10.5px] text-arena-700 dark:text-neon-200/70">#{r.id}</td>
                  <td className="px-2 py-2">
                    <div className={`font-bold ${isOverridden ? 'line-through' : ''} text-arena-900 dark:text-white`}>
                      {fullName}
                    </div>
                    {!isAgent && <div className="font-mono text-[10px] text-arena-500 dark:text-neon-200/50">@{r.username}</div>}
                  </td>
                  <td className="px-2 py-2 font-mono tabular-nums text-arena-700 dark:text-neon-200/75">
                    {r.phone || <span className="text-arena-400 dark:text-neon-200/70">—</span>}
                  </td>
                  <td className="px-2 py-2 text-arena-700 dark:text-neon-200/75">
                    {r.area_name || <span className="text-arena-400 dark:text-neon-200/70">—</span>}
                    {r.pincode && <span className="font-mono text-[10.5px] text-arena-500 dark:text-neon-200/50"> · {r.pincode}</span>}
                  </td>
                  <td className="px-2 py-2 text-arena-700 dark:text-neon-200/75">
                    {r.current_parent_username
                      ? `@${r.current_parent_username}`
                      : <span className="text-arena-400 dark:text-neon-200/70">admin-direct</span>}
                  </td>
                  <td className="px-2 py-2 text-right font-mono tabular-nums">
                    ₹{(r.wallet_balance || 0).toLocaleString('en-IN')}
                  </td>
                  {isAgent && (
                    <td className="px-2 py-2 text-right font-mono tabular-nums">
                      ₹{((r as any).deposit_balance || 0).toLocaleString('en-IN')}
                    </td>
                  )}
                  <td className="px-2 py-2">
                    <div className="font-mono tabular-nums text-arena-900 dark:text-white">{r.pending_since}</div>
                    <div className={`text-[10px] mt-0.5 font-bold ${isOldest ? 'text-rose-700 dark:text-rose-300' : 'text-arena-600 dark:text-neon-200/55'}`}>
                      stuck {stuckLabel}{isOldest ? ' ← oldest' : ''}
                    </div>
                  </td>
                  <td className="px-2 py-2">
                    <ReasonBadge reason={r.reason} isOverridden={isOverridden} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Tiny label:value row used by the mobile blocked-card layout.
 *  Keeps the visual rhythm consistent across Phone / Location /
 *  Current parent without inlining the same markup three times. */
function KV({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline gap-2">
      <div className="text-[9.5px] uppercase tracking-wider text-arena-500 dark:text-neon-200/50
                      font-semibold shrink-0 w-24">
        {label}
      </div>
      <div className={`text-arena-800 dark:text-neon-200/85 min-w-0 break-words
                       ${mono ? 'font-mono tabular-nums' : ''}`}>
        {value}
      </div>
    </div>
  );
}

function BlockedAvatar({ url, username }: { url: string | null; username: string }) {
  if (url) {
    return (
      <img src={url} alt={username}
           className="w-8 h-8 rounded-full object-cover ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40" />
    );
  }
  // Fallback initials avatar.
  const initial = (username || '?').slice(0, 1).toUpperCase();
  return (
    <div className="w-8 h-8 rounded-full bg-violet-100 dark:bg-violet-500/20
                    text-violet-700 dark:text-violet-200 grid place-items-center
                    text-[12px] font-bold ring-1 ring-inset ring-violet-200/40 dark:ring-violet-500/30">
      {initial}
    </div>
  );
}

// ─── Sub-components used only by GeoAutoReassignCard ───────────────

function SectionHeader({ icon, title, subtitle }: { icon: string; title: string; subtitle: string }) {
  return (
    <div className="pt-2 border-t border-arena-200/40 dark:border-arena-700/40">
      <div className="flex items-center gap-2 mt-2">
        <Icon name={icon} size={14} className="text-violet-600 dark:text-violet-300" />
        <h4 className="text-[12.5px] font-bold text-arena-900 dark:text-white">{title}</h4>
      </div>
      <p className="text-[11px] text-arena-600 dark:text-neon-200/55 mt-0.5 leading-relaxed">{subtitle}</p>
    </div>
  );
}

/** A single cell in the control center. Renders status icon +
 *  timestamp + last-result chip + Run button. */
function GeoCellCard({ cell, lastRun, disabled, isRunning, onRun }: {
  cell: GeoCell;
  lastRun: GeoLastRun | null;
  disabled: boolean;
  isRunning: boolean;
  onRun: () => void;
}) {
  // Status: never-run / fresh (< 1 day) / older / running.
  const statusIcon = isRunning ? 'progress_activity'
    : lastRun ? 'check_circle'
    : 'radio_button_unchecked';
  const statusColor = isRunning ? 'text-violet-600 dark:text-violet-300'
    : lastRun ? 'text-emerald-600 dark:text-emerald-400'
    : 'text-arena-400 dark:text-neon-200/40';
  const statusText = isRunning ? 'Running…'
    : lastRun ? 'Last run' : 'Never run';

  return (
    <div className="rounded-xl px-3 py-3 bg-arena-50/60 dark:bg-arena-800/40
                    ring-1 ring-inset ring-arena-200/50 dark:ring-arena-600/40
                    flex flex-col gap-2">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            {cell.step != null && (
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full
                                bg-violet-100 dark:bg-violet-500/20
                                text-violet-700 dark:text-violet-200
                                text-[10px] font-bold tabular-nums">
                {cell.step}
              </span>
            )}
            <span className="text-[12.5px] font-bold text-arena-900 dark:text-white truncate">
              {cell.title}
            </span>
          </div>
          <p className="text-[10.5px] text-arena-600 dark:text-neon-200/50 leading-snug mt-0.5">
            {cell.subtitle}
          </p>
        </div>
        <button onClick={onRun} disabled={disabled || isRunning}
                title={disabled && !isRunning ? 'Master toggle is OFF — enable above first' : 'Run this cell now'}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full
                           bg-violet-600 hover:bg-violet-500 active:bg-violet-700
                           text-white text-[11.5px] font-bold
                           shadow-[0_1px_4px_rgba(139,92,246,0.35)]
                           transition active:scale-95
                           disabled:opacity-40 disabled:cursor-not-allowed
                           disabled:shadow-none disabled:active:scale-100 shrink-0">
          {isRunning
            ? <Icon name="progress_activity" size={12} className="animate-spin" />
            : <Icon name="play_arrow" size={12} />}
          <span>{isRunning ? 'Running' : 'Run'}</span>
        </button>
      </div>

      {/* Status / timestamp row */}
      <div className="flex items-center gap-1.5 text-[11px]">
        <Icon name={statusIcon}
              size={13}
              className={`${statusColor} ${isRunning ? 'animate-spin' : ''}`} />
        <span className={statusColor + ' font-semibold'}>{statusText}</span>
        {lastRun && (
          <span className="font-mono tabular-nums text-arena-700 dark:text-neon-200/70">
            · {lastRun.ran_at}
          </span>
        )}
      </div>

      {/* Last-result chip */}
      {lastRun && (
        <div className="flex flex-wrap gap-1.5 text-[10.5px]">
          <Chip tone="neutral">{lastRun.eligible} eligible</Chip>
          <Chip tone={lastRun.moved > 0 ? 'emerald' : 'neutral'}>{lastRun.moved} moved</Chip>
          <Chip tone="neutral">{lastRun.unchanged} unchanged</Chip>
          {lastRun.unmatched > 0 && <Chip tone="amber">{lastRun.unmatched} unmatched</Chip>}
          {lastRun.skipped_level_cap > 0 && <Chip tone="amber">{lastRun.skipped_level_cap} level-cap skip</Chip>}
        </div>
      )}
    </div>
  );
}

function Chip({ tone, children }: { tone: 'emerald' | 'amber' | 'neutral'; children: React.ReactNode }) {
  const cls = tone === 'emerald'
    ? 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-200 ring-emerald-200/60 dark:ring-emerald-500/30'
    : tone === 'amber'
    ? 'bg-amber-50 dark:bg-amber-500/15 text-amber-800 dark:text-amber-200 ring-amber-200/60 dark:ring-amber-500/30'
    : 'bg-white/60 dark:bg-arena-700/40 text-arena-700 dark:text-neon-200/70 ring-arena-200/60 dark:ring-arena-600/40';
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md font-semibold tabular-nums ring-1 ring-inset ${cls}`}>
      {children}
    </span>
  );
}

// ════════════════════════════════════════════════════════════════════
// AgentTypeahead — re-usable agent picker
// ════════════════════════════════════════════════════════════════════
//
// Used in two places on this panel:
//   1. As the "Currently under (parent) agent" filter — narrows the
//      source rows to those owned by the picked agent. `acceptKind`
//      is NOT passed → server returns *all* active agents.
//   2. As the destination picker — `acceptKind = 'players' | 'agents'`
//      restricts to agents whose corresponding accept-flag is ON.
//
// UX behaviour (changed Phase R7 follow-up, 2026-05-13):
//   * Dropdown opens on focus AND fetches the top 30 agents
//     immediately with an empty `q` so the admin can BROWSE without
//     having to know a name. The earlier gate (`q.length < 1` → no
//     fetch) made the widget feel like a dead-end and was the source
//     of the "can't see which agents are available" complaint.
//   * Optional pincode pre-filter narrows the browse list to a 6-digit
//     pincode (admin can use this to "see agents in 482020").
//   * Each row carries username, UAID, level, area name, pincode,
//     and live counts (owned players / sub-agents). The level chip
//     and load badge are visually distinct so the admin can pick
//     the agent who has bandwidth.
//
// To change the page size: bump the `limit` query param below
// (server caps at 100 in adminPhase11G.controller.searchDestinationAgents).
// To change the row layout: edit the JSX between `key={a.id}` and the
// closing `</button>` — the data fields available are everything in
// the `DestAgent` interface defined near the top of this file.
function AgentTypeahead({ value, onChange, kind, acceptKind, pincode }: {
  value: DestAgent | null;
  onChange: (a: DestAgent | null) => void;
  kind: Kind;
  /**
   * When set ('players' | 'agents'), the server only returns agents
   * whose `is_accepting_<kind>` flag is ON. Used for the destination
   * picker. Omit for the source-row "current parent" filter, which
   * should show every active agent.
   */
  acceptKind?: Kind;
  /**
   * 6-digit India pincode. When set, the server filters destinations
   * to agents whose own pincode matches. Lets the admin scope to a
   * specific catchment area without typing.
   */
  pincode?: string;
}) {
  // `q` = free-text substring (matches username / UAID / email server-side).
  // `open` = dropdown visibility. We close on row-pick + Clear.
  // `results` = the rows shown in the dropdown.
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState<DestAgent[]>([]);
  const [busy, setBusy] = useState(false);

  // Fetch results whenever the dropdown is open OR the inputs change.
  // NOTE the dependency array — we deliberately also re-fetch when
  // `kind` / `acceptKind` / `pincode` change, so flipping mode at
  // the panel level refreshes this widget transparently.
  useEffect(() => {
    let abort = false;
    if (!open) return;                            // closed → no fetch
    setBusy(true);
    (async () => {
      try {
        const qs = new URLSearchParams();
        qs.set('kind', acceptKind || kind);
        if (q.trim()) qs.set('q', q.trim());      // empty q → browse all
        if (pincode)  qs.set('pincode', pincode);
        qs.set('limit', '30');
        const r = await adminApi.get<{ rows: DestAgent[] }>(`/admin/bulk-reassign/destinations?${qs.toString()}`);
        if (!abort) setResults(r.rows);
      } catch { /* parent surfaces the panel-wide error */ }
      finally { if (!abort) setBusy(false); }
    })();
    return () => { abort = true; };
  }, [q, open, kind, acceptKind, pincode]);

  // Close-on-blur with a tiny delay so clicking a row registers before
  // the dropdown unmounts. setTimeout(0) is unreliable across browsers;
  // 150ms is the conventional value that works on Safari + Chrome.
  function handleBlur() {
    setTimeout(() => setOpen(false), 150);
  }

  return (
    <div className="relative">
      <div className="flex gap-2">
        <input
          value={value ? `@${value.username}${value.uaid ? ' · ' + value.uaid : ''}` : q}
          onChange={(e) => { onChange(null); setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={handleBlur}
          placeholder={acceptKind
            ? "Click to browse — or type username, UAID, email"
            : "Click to browse — or type to filter"}
          className={inputCls}
        />
        {value && (
          <button onClick={() => { onChange(null); setQ(''); }}
                  className="text-[11.5px] px-2 rounded-md text-arena-600 dark:text-neon-200/60 hover:text-rose-600 transition">
            Clear
          </button>
        )}
      </div>
      {open && !value && (
        <div className="absolute z-20 left-0 right-0 mt-1 max-h-96 overflow-auto rounded-xl
                        bg-white dark:bg-arena-800
                        ring-1 ring-inset ring-arena-200/60 dark:ring-arena-600/40 shadow-xl backdrop-blur">
          {/* Header row: tells the admin how the list is filtered. */}
          <div className="sticky top-0 z-10 px-3 py-2 text-[10.5px] uppercase tracking-wider font-semibold
                          bg-arena-50/80 dark:bg-arena-800/80 backdrop-blur
                          border-b border-arena-200/50 dark:border-arena-600/40
                          text-arena-600 dark:text-neon-200/60 flex items-center justify-between">
            <span>
              {acceptKind ? `Accepting ${acceptKind}` : 'All active agents'}
              {pincode && ` · pincode ${pincode}`}
              {q.trim() && ` · "${q.trim()}"`}
            </span>
            <span className="tabular-nums">{busy ? '…' : `${results.length} shown`}</span>
          </div>
          {busy && results.length === 0 && (
            <div className="px-3 py-3 text-[11.5px] text-arena-600 dark:text-neon-200/60">Loading agents…</div>
          )}
          {!busy && results.length === 0 && (
            <div className="px-3 py-4 text-[11.5px] text-arena-600 dark:text-neon-200/60">
              No agents match {pincode || q.trim() ? 'these filters' : 'the accepting-toggle filter'}.
              {acceptKind && <> Tell at least one agent to switch ON <b>Accepting {acceptKind}</b>.</>}
            </div>
          )}
          {/* Each row is rendered as a clickable button for keyboard
              accessibility. On click we set the selected DestAgent
              upward via onChange. */}
          {results.map(a => (
            <button key={a.id} onMouseDown={(e) => e.preventDefault() /* keep input focused so onBlur doesn't kill the click */}
                    onClick={() => { onChange(a); setOpen(false); setQ(''); }}
                    className="block w-full text-left px-3 py-2.5 border-b last:border-0
                               border-arena-200/40 dark:border-arena-700/40
                               hover:bg-violet-50/60 dark:hover:bg-violet-500/10 transition">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-bold text-arena-900 dark:text-white truncate">
                    @{a.username}
                    <span className="ml-2 inline-block text-[10px] font-mono px-1.5 py-0.5 rounded
                                     bg-arena-100/60 dark:bg-arena-700/50
                                     text-arena-600 dark:text-neon-200/65">{a.uaid}</span>
                    <span className="ml-2 inline-block text-[10px] font-bold px-1.5 py-0.5 rounded
                                     bg-violet-100 dark:bg-violet-500/20
                                     text-violet-700 dark:text-violet-200">{levelName(a.level)}</span>
                  </div>
                  <div className="text-[11.5px] text-arena-700 dark:text-neon-200/75 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-0.5">
                      <Icon name="place" size={11} />
                      {a.area_name || <span className="text-arena-400 dark:text-neon-200/70">unknown area</span>}
                    </span>
                    {a.pincode && (
                      <span className="inline-flex items-center gap-0.5 font-mono">
                        <Icon name="local_post_office" size={11} />
                        {a.pincode}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right text-[10.5px] text-arena-600 dark:text-neon-200/60 shrink-0">
                  <div className="font-bold tabular-nums">{a.owned_players}<span className="font-normal text-arena-400 dark:text-neon-200/70"> players</span></div>
                  <div className="font-bold tabular-nums">{a.owned_subagents}<span className="font-normal text-arena-400 dark:text-neon-200/70"> sub-agents</span></div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Stat / KindBtn / Field / Banner / inputCls (preserved) ──────
function Stat({ label, value, tone }: { label: string; value: number; tone?: 'emerald' | 'amber' }) {
  const colour = tone === 'emerald' ? 'text-emerald-600 dark:text-emerald-300'
               : tone === 'amber'   ? 'text-amber-600 dark:text-amber-300'
               : 'text-arena-900 dark:text-white';
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60 font-semibold">{label}</div>
      <div className={`text-[15px] font-extrabold tabular-nums ${colour}`}>{value}</div>
    </div>
  );
}
function KindBtn({ active, onClick, icon, label }: { active: boolean; onClick: () => void; icon: string; label: string }) {
  return (
    <button onClick={onClick}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-semibold transition ring-1 ring-inset ${
              active
                ? 'bg-violet-600 text-white ring-violet-600 shadow-[0_2px_10px_rgba(139,92,246,0.4)]'
                : 'bg-white text-arena-700 ring-arena-200/60 hover:bg-arena-50 dark:bg-arena-700/40 dark:text-neon-200/80 dark:ring-arena-600/40 dark:hover:bg-arena-700/60'
            }`}>
      <Icon name={icon} size={13} /> {label}
    </button>
  );
}
// 2026-05-20 — aliased to canonical INPUT_CLS for dark-mode legibility.
const inputCls = INPUT_CLS;
function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[10.5px] uppercase tracking-wider text-arena-600 dark:text-neon-200/60 mb-1 font-semibold">{label}</span>
      {children}
      {hint && <p className="text-[10.5px] text-arena-600 dark:text-neon-200/50 mt-1">{hint}</p>}
    </label>
  );
}
function Banner({ tone, children }: { tone: 'error' | 'success' | 'info'; children: React.ReactNode }) {
  const m = tone === 'error'
    ? 'bg-rose-50 dark:bg-rose-500/10 ring-rose-200/60 dark:ring-rose-400/30 text-rose-700 dark:text-rose-200'
    : tone === 'success' ? 'bg-emerald-50 dark:bg-emerald-500/10 ring-emerald-200/60 dark:ring-emerald-400/30 text-emerald-700 dark:text-emerald-200'
    : 'bg-arena-50/70 dark:bg-arena-800/60 ring-arena-200/60 dark:ring-arena-600/40 text-arena-800 dark:text-neon-200/80';
  return <div className={`rounded-2xl ring-1 ring-inset px-4 py-3 text-[13px] ${m}`}>{children}</div>;
}
